/**
 * Document Service
 * Handles PDF and TXT file parsing and extracted text management
 */
import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url
).toString();

class DocumentService {
    constructor() {
        this.currentDocument = null;
        this.extractedText = '';
        this.pageCount = 0;
        this.fileName = '';
        this.fileType = ''; // 'pdf' or 'txt'
    }

    /**
     * Load and extract text from a file (PDF or TXT)
     * @param {File} file - The file to process
     * @param {function} onProgress - Progress callback (percent, pageNumber)
     * @returns {Promise<{text: string, pageCount: number, fileName: string, fileType: string}>}
     */
    async extractText(file, onProgress = () => { }) {
        if (!file) {
            throw new Error('Invalid file');
        }

        this.fileName = file.name;

        // Determine file type based on extension or MIME type
        if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
            this.fileType = 'pdf';
        } else if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
            this.fileType = 'txt';
        } else {
            throw new Error('Unsupported file type. Please use PDF or TXT.');
        }

        // Reset state
        this.extractedText = '';
        this.pageCount = 0;
        this.currentDocument = null;

        try {
            if (this.fileType === 'pdf') {
                return await this._extractPDF(file, onProgress);
            } else {
                return await this._extractTXT(file, onProgress);
            }
        } catch (error) {
            console.error('Document extraction error:', error);
            throw new Error(`Failed to extract text from document: ${error.message}`);
        }
    }

    /**
     * Internal method to extract text from PDF
     */
    async _extractPDF(file, onProgress) {
        // Read file as ArrayBuffer
        const arrayBuffer = await file.arrayBuffer();

        // Load PDF document
        const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
        this.currentDocument = await loadingTask.promise;
        this.pageCount = this.currentDocument.numPages;

        let fullText = '';

        // Extract text from each page
        for (let pageNum = 1; pageNum <= this.pageCount; pageNum++) {
            const page = await this.currentDocument.getPage(pageNum);
            const textContent = await page.getTextContent();

            // Concatenate text items
            const pageText = textContent.items
                .map(item => item.str)
                .join(' ')
                .replace(/\s+/g, ' ')
                .trim();

            if (pageText) {
                fullText += `[Page ${pageNum}]\n${pageText}\n\n`;
            }

            // Report progress
            const progress = Math.round((pageNum / this.pageCount) * 100);
            onProgress(progress, pageNum);
        }

        this.extractedText = fullText.trim();

        return {
            text: this.extractedText,
            pageCount: this.pageCount,
            fileName: this.fileName,
            fileType: 'pdf'
        };
    }

    /**
     * Internal method to extract text from TXT file
     */
    async _extractTXT(file, onProgress) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onprogress = (event) => {
                if (event.lengthComputable) {
                    const progress = Math.round((event.loaded / event.total) * 100);
                    onProgress(progress, 1);
                }
            };

            reader.onload = (event) => {
                this.extractedText = event.target.result;
                this.pageCount = 1; // TXT files count as 1 "page"
                this.currentDocument = true; // Just a flag that we have a doc

                onProgress(100, 1);

                resolve({
                    text: this.extractedText,
                    pageCount: 1,
                    fileName: this.fileName,
                    fileType: 'txt'
                });
            };

            reader.onerror = (error) => {
                reject(error);
            };

            reader.readAsText(file);
        });
    }

    /**
     * Get a summary of the extracted text (truncated for context)
     * @param {number} maxLength - Maximum character length
     * @returns {string}
     */
    getContextSummary(maxLength = 4000) {
        if (!this.extractedText) return '';

        if (this.extractedText.length <= maxLength) {
            return this.extractedText;
        }

        // Truncate intelligently at word boundary
        const truncated = this.extractedText.substring(0, maxLength);
        const lastSpace = truncated.lastIndexOf(' ');
        return truncated.substring(0, lastSpace) + '... [contenu tronqué]';
    }

    /**
     * Split text into chunks for RAG
     * @param {number} chunkSize - Target chunk size in chars
     * @param {number} overlap - Overlap between chunks in chars
     * @returns {string[]} - Array of text chunks
     */
    getChunks(chunkSize = 500, overlap = 50) {
        if (!this.extractedText) return [];

        const text = this.extractedText;
        const chunks = [];
        let start = 0;

        while (start < text.length) {
            let end = start + chunkSize;

            // Don't cut in the middle of a word
            if (end < text.length) {
                const nextSpace = text.indexOf(' ', end);
                if (nextSpace !== -1 && nextSpace - end < 50) {
                    end = nextSpace;
                }
            } else {
                end = text.length;
            }

            const chunk = text.substring(start, end).trim();
            if (chunk.length > 20) { // Skip very small chunks
                chunks.push(chunk);
            }

            start = end - overlap;
            if (start < 0) start = 0;
            if (start >= text.length) break;
        }

        console.log(`Document chunked into ${chunks.length} chunks (~${chunkSize} chars each)`);
        return chunks;
    }

    /**
     * Check if a document is currently loaded
     * @returns {boolean}
     */
    hasDocument() {
        return this.extractedText.length > 0;
    }

    /**
     * Get document info
     * @returns {object}
     */
    getInfo() {
        return {
            fileName: this.fileName,
            fileType: this.fileType,
            pageCount: this.pageCount,
            textLength: this.extractedText.length
        };
    }

    /**
     * Clear the current document
     */
    clear() {
        this.currentDocument = null;
        this.extractedText = '';
        this.pageCount = 0;
        this.fileName = '';
        this.fileType = '';
    }
}

// Export singleton instance
export const documentService = new DocumentService();
export default documentService;
